import React, { useState } from 'react';
import '../styles/SearchBar.css';
import { geocodeAddress } from '../services/geocoding';

function SearchBar({ onSearch, selectedRadius, inNavBar = false, searchLocation = null }) {
  const [searchInput, setSearchInput] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mismatchNotice, setMismatchNotice] = useState(null);

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(null), 8000);
    setIsLoading(false);
  };

  const checkMismatch = (input, location) => {
    const isZipCode = /^\d{5}(-\d{4})?$/.test(input);
    if (isZipCode) return; // zip codes are already validated

    const resolvedName = (location.cityState || location.name || '').toLowerCase();
    const inputLower = input.toLowerCase();

    // Check if the input appears in the resolved location name
    const inputWords = inputLower.split(/[\s,]+/).filter(w => w.length >= 3);
    const hasMatch = inputWords.some(word => resolvedName.includes(word));

    if (!hasMatch) {
      setMismatchNotice({
        input: input,
        resolved: location.cityState || location.name
      });
    } else {
      setMismatchNotice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const input = searchInput.trim();
    if (!input) return;

    // Input validation
    const isAllDigits = /^\d+$/.test(input);
    const isZipCode = /^\d{5}(-\d{4})?$/.test(input);

    // Pure numbers that aren't a valid 5-digit zip code
    if (isAllDigits && !isZipCode) {
      showError('Please enter a valid 5-digit US zip code or a US city/state.');
      return;
    }

    // Too short for a non-zip address
    if (!isZipCode && input.length < 3) {
      showError('Please enter at least 3 characters for an address search.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setMismatchNotice(null);

    try {
      const location = await geocodeAddress(searchInput);

      if (!location) {
        showError(isZipCode
          ? 'Zip code not found. Please enter a valid US zip code.'
          : 'Location not found in the US. Please enter a valid US zip code or city/state.');
        return;
      }

      checkMismatch(input, location);
      onSearch(location, selectedRadius);
      setIsLoading(false);
    } catch (err) {
      showError('Something went wrong. Please try again.');
    }
  };

  return (
    <div className={`search-container ${inNavBar ? 'in-nav' : ''}`}>
      <form className="search-bar" onSubmit={handleSubmit}>
        <input
          type="text"
          className="search-input"
          placeholder="Enter US zip code or city/state..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button
          type="submit"
          className="search-submit"
          disabled={isLoading}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="2"/>
            <line x1="11" y1="11" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </form>

      {searchLocation && inNavBar && (
        <div className="search-results-info">
          Showing results for {searchLocation.cityState || searchLocation.name}
        </div>
      )}

      {mismatchNotice && (
        <div className="mismatch-notice">
          <span className="mismatch-text">
            No exact match for "<strong>{mismatchNotice.input}</strong>".
            Showing results for <strong>{mismatchNotice.resolved}</strong>.
          </span>
          <button
            className="mismatch-dismiss"
            onClick={() => setMismatchNotice(null)}
            aria-label="Dismiss notice"
          >
            &times;
          </button>
        </div>
      )}

      {error && (
        <div className="error-message">
          <div className="error-icon">!</div>
          <div className="error-content">
            <div className="error-title">Error!</div>
            <div className="error-text">{error}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
